import { Chart, ChartOptions } from "chart.js/auto"

function* colorGenerator(baseColorHex: string, count: number) {
    // strip the leading # if it's there
    baseColorHex = baseColorHex.replace(/^\s*#|\s*$/g, "")

    // convert 3 char codes --> 6, e.g. `E0F` --> `EE00FF`
    if (baseColorHex.length === 3) {
        baseColorHex = baseColorHex.replace(/(.)/g, "$1$1")
    }

    let colors = [
        parseInt(baseColorHex.substr(0, 2), 16),
        parseInt(baseColorHex.substr(2, 2), 16),
        parseInt(baseColorHex.substr(4, 2), 16),
    ]

    const topValue = 220
    const tailValue = 90
    const rangeSize = Math.floor((topValue - tailValue) / count)

    const maxValue = Math.max.apply(Math, colors)
    const diffToTop = topValue - maxValue
    colors = colors.map((c) => c + diffToTop)

    for (let i = 0; i < count; i++) {
        yield `#${colors
            .map((c) => Math.abs(c - rangeSize * i))
            .map((c) => c.toString(16))
            .map((c) => (c.length < 2 ? `0${c}` : c))
            .join("")}`
    }
}

const getDefaultColors = (domElement: Element, count: number, mode: string) => {
    const accessor = `--bs-${mode}`
    const baseColor = getComputedStyle(domElement)
        .getPropertyValue(accessor)
        .trim()

    return Array.from(colorGenerator(baseColor, count))
}

const handleColorDefinition = (oColor, elementCount, domElement) => {
    if (typeof oColor !== "object" || oColor == null) {
        oColor = { themeColor: "primary" }
    }

    // Thememfarbe gesetzt
    if ("themeColor" in oColor) {
        return getDefaultColors(
            domElement,
            oColor.single ? 1 : elementCount,
            oColor.themeColor
        )
    }
    // Einzelne Farben gesetzt
    if ("colors" in oColor) {
        const useColors = oColor.colors
        if (useColors.length === 1) return useColors[0]
        return useColors
    }

    // Fallback
    return getDefaultColors(domElement, elementCount, "primary")
}

function formatTextWithPlaceholder(text: string, value: number) {
    return text.replace("{0}", value.toString())
}

export function initalizeDiagrams() {
    const uninitializedCharts = document.querySelectorAll(
        ".chart-inital"
    ) as NodeListOf<HTMLDivElement>

    for (let element of uninitializedCharts) {
        element.className = "chart"

        const scales = {}

        const json = JSON.parse(element.dataset.chart)
        const canvas = element.querySelector("canvas")
        const datasets = []

        for (let i = 0; i < json.datasets.length; i++) {
            let dataset = json.datasets[i]
            dataset.backgroundColor = handleColorDefinition(
                dataset.backgroundColor,
                dataset.data.length,
                element
            )
            dataset.borderColor = handleColorDefinition(
                dataset.borderColor,
                dataset.data.length,
                element
            )

            dataset.yAxisID = `y${i}`
            dataset.tooltipFormat = dataset.tooltipFormat || "{0}"
            datasets.push(dataset)

            if (
                dataset.scaleText != undefined &&
                dataset.scaleTicks != undefined &&
                i < 2
            ) {
                scales[`y${i}`] = {
                    type: "linear",
                    position: i == 0 ? "left" : "right",
                    title: {
                        display: true,
                        text: dataset.scaleText,
                    },
                    grid: {
                        drawOnChartArea: i == 0,
                    },
                    ticks: {
                        callback: (value) =>
                            formatTextWithPlaceholder(
                                dataset.scaleTicks,
                                value
                            ),
                    },
                }
            }
        }

        const data = {
            labels: json.labels,
            datasets,
        }

        const options: ChartOptions<"line"> = {
            plugins: {
                legend: {
                    display: json.options.legend,
                    onClick: function (e, legendItem, legend) {
                        const index = legendItem.datasetIndex
                        const ci = legend.chart
                        const meta = ci.getDatasetMeta(index)
                        meta.hidden =
                            meta.hidden === null
                                ? !ci.data.datasets[index].hidden
                                : null

                        if (index === 0) {
                            ci.options.scales.y.display = !meta.hidden
                        } else if (index === 1) {
                            ci.options.scales.y1.display = !meta.hidden
                        }
                        ci.update()
                    },
                },
                tooltip: {
                    callbacks: {
                        label: function (context) {
                            let label = context.dataset.label || ""
                            if (label) {
                                label += ": "
                            }

                            const yValue = context.parsed.y

                            if (
                                (context.dataset as any).tooltipFormat ==
                                undefined
                            ) {
                                return label + yValue
                            }

                            return (
                                label +
                                formatTextWithPlaceholder(
                                    (context.dataset as any).tooltipFormat,
                                    yValue
                                )
                            )
                        },
                    },
                },
            },
            scales,
            responsive: true,
            resizeDelay: 50,
            maintainAspectRatio: false,
        }

        const config = {
            type: json.type,
            data,
            options,
        }

        new Chart(canvas, config)
    }
}
